/*
 * The agent's one line once its work is closed (LEA-225): what closed it, who and when, and a quiet
 * way to reopen the draft. Done is now what closes a conversation's draft; a No response or Deck only
 * recorded before it reads the same way, so every closed conversation says it in one line.
 */
import type { ReactNode } from "react";
import type { WorkPlan } from "../../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import { CheckIcon, ExternalLinkIcon } from "../icons.tsx";
import { whenLabel } from "./conversation-shared.ts";

type Outcome = NonNullable<WorkPlan["operatorOutcome"]>;

/** "Closed when marked done", or what an outcome recorded before Done closed work said. */
function closedBy(outcome: Outcome, byDone: boolean): string {
  if (byDone) return "Closed when marked done";
  return outcome.kind === "deck_only" ? "Closed for the deck only" : "Closed with no reply";
}

export function ClosedLine(props: {
  outcome: Outcome;
  /** The Done that closed it, while it is the plan's current outcome (`currentDoneClosure`). */
  doneClosure: { doneAt: string; doneBy: string } | null;
  viewerId: string | null;
  bisonUrl: string | null;
  busy: boolean;
  onReopen: () => void;
  /** What the item adds to the line, such as Show activity. */
  children?: ReactNode;
}) {
  const { outcome, doneClosure } = props;
  const actor = doneClosure?.doneBy ?? outcome.actorId;
  const at = doneClosure?.doneAt ?? outcome.reviewedAt;
  return (
    <div className="lcr-agent-draft-line lcr-agent-closed-line">
      <CheckIcon size={16} />
      <span role="status">
        {closedBy(outcome, doneClosure !== null)} · {actor === props.viewerId ? "You" : "A rep"},{" "}
        {whenLabel(at)}
      </span>
      <span className="lcr-agent-quiet">
        {outcome.kind === "deck_only" && props.bisonUrl && (
          <a
            className="lcr-small-button lcr-ghost"
            href={props.bisonUrl}
            target="_blank"
            rel="noreferrer"
          >
            <ExternalLinkIcon size={16} /> Open Bison
          </a>
        )}
        <button
          type="button"
          className="lcr-small-button lcr-ghost"
          disabled={props.busy}
          onClick={props.onReopen}
        >
          Reopen draft
        </button>
        {props.children}
      </span>
    </div>
  );
}
